import { ArrowRight, Award, BadgeCheck, ShieldAlert, Star, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Generic structured-AI renderer. Every AI endpoint returns an array of
 * these blocks instead of a wall of HTML, so each page can render real
 * product UI with cards, chips, and CTAs.
 *
 * Block taxonomy (kept tight on purpose — anything that doesn't fit
 * cleanly should be a `paragraph`):
 *   - heading      : section header
 *   - paragraph    : prose
 *   - list         : bulleted points
 *   - chips        : keyword chips (titles, tags, alt names)
 *   - callout      : tone-coded advisory (good / warn / bad)
 *   - vendor       : structured supplier card with feedback / orders / etc.
 *   - opportunity  : product-suggestion card
 */

export type AIBlock =
  | { type: 'heading'; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'list'; items: string[] }
  | { type: 'chips'; label?: string; items: string[] }
  | { type: 'callout'; tone: 'good' | 'warn' | 'bad'; text: string }
  | {
      type: 'vendor';
      name: string;
      feedbackPct?: number;
      orders?: number;
      shipDays?: string;
      returnsPolicy?: string;
      priceCad?: number;
      badge?: 'best' | 'avoid' | null;
      reasoning?: string;
    }
  | {
      type: 'opportunity';
      title: string;
      category?: string;
      demand?: 'Low' | 'Medium' | 'High';
      priceMin?: number;
      priceMax?: number;
      supplier?: string;
      reasoning?: string;
    };

function fmtCad(n: number): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(n);
}

function Heading({ text }: { text: string }): JSX.Element {
  return (
    <h3
      style={{
        fontSize: 'var(--text-md)',
        fontWeight: 600,
        color: 'var(--c-text-secondary)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        margin: 0,
      }}
    >
      {text}
    </h3>
  );
}

function Paragraph({ text }: { text: string }): JSX.Element {
  return (
    <p style={{ fontSize: 'var(--text-md)', color: 'var(--c-text)', margin: 0, lineHeight: 1.6 }}>
      {text}
    </p>
  );
}

function BulletList({ items }: { items: string[] }): JSX.Element {
  return (
    <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
      {items.map((it, i) => (
        <li key={i} style={{ fontSize: 'var(--text-md)', lineHeight: 1.55 }}>
          {it}
        </li>
      ))}
    </ul>
  );
}

function Chips({ label, items }: { label?: string; items: string[] }): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {label && (
        <span
          style={{
            fontSize: 'var(--text-sm)',
            fontWeight: 500,
            color: 'var(--c-muted)',
          }}
        >
          {label}
        </span>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {items.map((it, i) => (
          <span
            key={i}
            style={{
              fontSize: 'var(--text-base)',
              padding: '5px 10px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--c-primary-soft)',
              color: 'var(--c-primary-dark)',
              fontWeight: 500,
              border: '1px solid var(--c-primary-soft-border)',
            }}
          >
            {it}
          </span>
        ))}
      </div>
    </div>
  );
}

function Callout({ tone, text }: { tone: 'good' | 'warn' | 'bad'; text: string }): JSX.Element {
  const palette = {
    good: { bg: 'var(--c-success-bg)', fg: 'var(--c-success)', icon: BadgeCheck },
    warn: { bg: 'var(--c-warn-bg)', fg: 'var(--c-warn)', icon: ShieldAlert },
    bad: { bg: 'var(--c-danger-bg)', fg: 'var(--c-danger)', icon: ShieldAlert },
  }[tone];
  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        padding: 12,
        borderRadius: 'var(--radius-md)',
        background: palette.bg,
        color: palette.fg,
        fontSize: 'var(--text-md)',
        lineHeight: 1.5,
      }}
    >
      <palette.icon size={18} style={{ flexShrink: 0, marginTop: 1 }} />
      <span>{text}</span>
    </div>
  );
}

function Vendor({ b }: { b: Extract<AIBlock, { type: 'vendor' }> }): JSX.Element {
  const badge = b.badge;
  return (
    <div
      style={{
        padding: 14,
        border: badge === 'best' ? '1px solid var(--c-success-border)' : '1px solid var(--c-border)',
        borderRadius: 'var(--radius-lg)',
        background: badge === 'best' ? 'var(--c-success-soft-bg)' : 'var(--c-card)',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 'var(--text-md)', fontWeight: 600 }}>{b.name}</span>
        {badge === 'best' && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 'var(--text-xs)',
              background: 'var(--c-success-bg)',
              color: 'var(--c-success)',
              padding: '2px 8px',
              borderRadius: 99,
              fontWeight: 600,
            }}
          >
            <Award size={11} strokeWidth={2.4} /> Best pick
          </span>
        )}
        {badge === 'avoid' && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 'var(--text-xs)',
              background: 'var(--c-danger-bg)',
              color: 'var(--c-danger)',
              padding: '2px 8px',
              borderRadius: 99,
              fontWeight: 600,
            }}
          >
            <ShieldAlert size={11} strokeWidth={2.4} /> Avoid
          </span>
        )}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
          gap: 10,
          fontSize: 'var(--text-base)',
        }}
      >
        {b.feedbackPct !== undefined && (
          <div>
            <div style={{ color: 'var(--c-muted)', fontSize: 'var(--text-xs)' }}>Feedback</div>
            <div style={{ fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Star size={12} color="var(--c-warn-border)" fill="var(--c-warn-border)" />
              {b.feedbackPct}%
            </div>
          </div>
        )}
        {b.orders !== undefined && (
          <div>
            <div style={{ color: 'var(--c-muted)', fontSize: 'var(--text-xs)' }}>Orders</div>
            <div style={{ fontWeight: 600 }}>{b.orders.toLocaleString()}</div>
          </div>
        )}
        {b.shipDays && (
          <div>
            <div style={{ color: 'var(--c-muted)', fontSize: 'var(--text-xs)' }}>Ship to CA</div>
            <div style={{ fontWeight: 600 }}>{b.shipDays}</div>
          </div>
        )}
        {b.returnsPolicy && (
          <div>
            <div style={{ color: 'var(--c-muted)', fontSize: 'var(--text-xs)' }}>Returns</div>
            <div style={{ fontWeight: 600 }}>{b.returnsPolicy}</div>
          </div>
        )}
        {b.priceCad !== undefined && (
          <div>
            <div style={{ color: 'var(--c-muted)', fontSize: 'var(--text-xs)' }}>Price</div>
            <div style={{ fontWeight: 600 }}>{fmtCad(b.priceCad)}</div>
          </div>
        )}
      </div>
      {b.reasoning && (
        <div style={{ fontSize: 'var(--text-base)', color: 'var(--c-muted)', lineHeight: 1.5 }}>
          {b.reasoning}
        </div>
      )}
    </div>
  );
}

const DEMAND_TONE = {
  High: { bg: 'var(--c-success-bg)', fg: 'var(--c-success)' },
  Medium: { bg: 'var(--c-warn-bg)', fg: 'var(--c-warn)' },
  Low: { bg: 'var(--c-divider)', fg: 'var(--c-muted)' },
};

function Opportunity({
  b,
}: {
  b: Extract<AIBlock, { type: 'opportunity' }>;
}): JSX.Element {
  const tone = b.demand ? DEMAND_TONE[b.demand] : null;
  return (
    <div
      style={{
        padding: 14,
        border: '1px solid var(--c-border)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--c-card)',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          {b.category && (
            <div
              style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--c-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                fontWeight: 600,
                marginBottom: 4,
              }}
            >
              {b.category}
            </div>
          )}
          <div style={{ fontSize: 'var(--text-md)', fontWeight: 600 }}>{b.title}</div>
        </div>
        {tone && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 'var(--text-xs)',
              background: tone.bg,
              color: tone.fg,
              padding: '2px 8px',
              borderRadius: 99,
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            <TrendingUp size={11} strokeWidth={2.4} />
            {b.demand}
          </span>
        )}
      </div>
      {b.reasoning && (
        <div style={{ fontSize: 'var(--text-base)', color: 'var(--c-muted)', lineHeight: 1.5 }}>
          {b.reasoning}
        </div>
      )}
      {(b.priceMin !== undefined || b.supplier) && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingTop: 8,
            borderTop: '1px solid var(--c-divider)',
          }}
        >
          {b.priceMin !== undefined && b.priceMax !== undefined && (
            <div>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--c-muted)' }}>Sell price</div>
              <div style={{ fontWeight: 600 }}>
                {fmtCad(b.priceMin)} – {fmtCad(b.priceMax)}
              </div>
            </div>
          )}
          {b.supplier && (
            <span
              style={{
                fontSize: 'var(--text-xs)',
                fontWeight: 600,
                color: 'var(--c-text-secondary)',
                background: 'var(--c-divider)',
                padding: '4px 10px',
                borderRadius: 99,
              }}
            >
              {b.supplier}
            </span>
          )}
        </div>
      )}
      <Link
        to={`/research?q=${encodeURIComponent(b.title)}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          height: 30,
          fontSize: 'var(--text-base)',
          fontWeight: 500,
          background: 'var(--c-primary-soft)',
          color: 'var(--c-primary)',
          borderRadius: 'var(--radius-md)',
          textDecoration: 'none',
        }}
      >
        Research this
        <ArrowRight size={12} strokeWidth={2.4} />
      </Link>
    </div>
  );
}

export function AIBlocks({ blocks }: { blocks: AIBlock[] }): JSX.Element {
  // Group consecutive vendor / opportunity blocks into grids so they render
  // side-by-side instead of stacking vertically.
  const out: JSX.Element[] = [];
  let i = 0;
  while (i < blocks.length) {
    const b = blocks[i];
    if (!b) break;
    if (b.type === 'vendor' || b.type === 'opportunity') {
      const kind = b.type;
      const group: typeof blocks = [];
      while (i < blocks.length && blocks[i]?.type === kind) {
        group.push(blocks[i] as (typeof blocks)[number]);
        i++;
      }
      out.push(
        <div
          key={`grp-${i}`}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: 12,
          }}
        >
          {group.map((g, gi) =>
            g.type === 'vendor' ? (
              <Vendor key={gi} b={g} />
            ) : (
              <Opportunity key={gi} b={g as Extract<AIBlock, { type: 'opportunity' }>} />
            ),
          )}
        </div>,
      );
      continue;
    }
    if (b.type === 'heading') out.push(<Heading key={i} text={b.text} />);
    else if (b.type === 'paragraph') out.push(<Paragraph key={i} text={b.text} />);
    else if (b.type === 'list') out.push(<BulletList key={i} items={b.items} />);
    else if (b.type === 'chips')
      out.push(<Chips key={i} {...(b.label !== undefined && { label: b.label })} items={b.items} />);
    else if (b.type === 'callout') out.push(<Callout key={i} tone={b.tone} text={b.text} />);
    i++;
  }
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>{out}</div>;
}
